import React from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import sortBy from 'lodash/sortBy';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import ConstituentIcons from '@/lib/configs/ConstituentIcons';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import useDebouncedEffect from '@/lib/hooks/useDebouncedEffect';
import translate from '@/lib/intl/translate';
import type CustomField from '@/types/api/CustomField';
import { ALL_CONSTITUENTS_VALUE } from '../config/customFieldsConfig';
import type CustomFieldsCategory from '../types/CustomFieldsCategory';

const SEARCHABLE_FIELD_TYPES = [
  CustomFieldTypes.CHECKBOX,
  CustomFieldTypes.LIST_MULTI,
  CustomFieldTypes.LIST_SINGLE,
];

const getCategories = (): CustomFieldsCategory[] => [
  {
    label: translate('ALL_CONSTITUENTS'),
    value: ALL_CONSTITUENTS_VALUE,
    customFields: [],
  },
  ...sortBy(
    ConstituentIcons.map(({ label, value }) => ({
      label,
      value,
      customFields: [],
    })),
    'label',
  ),
];

export const mapFieldsToCategories = (fields: CustomField[]) => {
  const categories = getCategories();

  // Add fields to their respective categories.
  fields.forEach((customField) => {
    const categoryIndex = categories.findIndex(
      (category) =>
        category.value === customField.fieldResources?.[0]?.resourceValues?.[0],
    );
    const index = categoryIndex > 0 ? categoryIndex : 0;
    categories[index].customFields.push(customField);
  });

  // Sort fields within their categories according to their `sortOrder` index
  categories.forEach(({ customFields }) =>
    customFields.sort((a, b) => a.sortOrder - b.sortOrder),
  );
  return categories;
};

export default function useSortedCustomFields(
  includeOnlySearchableFields = false,
  searchParams: object = {},
) {
  const { data, update } = useCustomFields(searchParams);

  const fields = React.useMemo(
    () =>
      includeOnlySearchableFields
        ? (data || []).filter(({ fieldType }) =>
            SEARCHABLE_FIELD_TYPES.includes(fieldType),
          )
        : data || [],
    [data, includeOnlySearchableFields],
  );
  const noCustomFields = fields.length === 0;

  const [sortedCustomFields, setSortedCustomFields] = React.useState<
    CustomFieldsCategory[]
  >([]);

  useDebouncedEffect(
    () => {
      const newData = mapFieldsToCategories(fields);
      setSortedCustomFields(newData);
    },
    [fields, setSortedCustomFields],
    300,
  );

  const updateOrder = React.useCallback(
    async ({
      category,
      newIndex,
      oldIndex,
    }: {
      category: string;
      newIndex: number;
      oldIndex: number;
    }) => {
      const oldFields = sortedCustomFields.find(
        ({ value }) => value === category,
      )?.customFields;
      if (!oldFields) return;
      const newFields = arrayMove(oldFields, oldIndex, newIndex).map(
        (fieldItem, i) => ({ ...fieldItem, sortOrder: i }),
      );
      const newSortedCustomFields = sortedCustomFields.map((item) =>
        item.value === category ? { ...item, customFields: newFields } : item,
      );
      // update local state
      setSortedCustomFields(newSortedCustomFields);
    },
    [setSortedCustomFields, sortedCustomFields, update],
  );

  useDebouncedEffect(
    () => {
      const flattenedFields: CustomField[] = [];
      sortedCustomFields.forEach(({ customFields }) =>
        flattenedFields.push(...customFields),
      );
      const dirtyFields: CustomField[] = flattenedFields.filter(
        ({ customFieldId, sortOrder }) =>
          data?.find((oldField) => oldField.customFieldId === customFieldId)
            ?.sortOrder !== sortOrder,
      );
      dirtyFields.forEach(({ customFieldId, sortOrder }) =>
        update(customFieldId, { sortOrder }),
      );
    },
    [data, sortedCustomFields, update],
    2000,
  );

  return { isLoading: !data, noCustomFields, sortedCustomFields, updateOrder };
}
